// Centralized API client for identity/auth endpoints with environment-based base URLs

import { SetPasswordRequestBody } from '../models/requestBodies/SetPasswordRequestBody';
import { LoginRequestBody } from '../models/requestBodies/LoginRequestBody';
import { CreateNewUserRequestBody } from '../models/requestBodies/CreateNewUserRequestBody';
import { User } from '../models/User';
import { LinkUserToDriverRequestBody } from '../models/requestBodies/LinkUserToDriverRequestBody';
import { BaseClient } from './BaseClient';
import { CreditPackageProduct } from '../models/CreditPackageProduct';
import { PaymentIntentRequest } from '../models/requestBodies/PaymentIntentRequest';
import { PaymentIntentResponse } from '../models/responseBodies/PaymentIntentResponse';
import { UserEmailUpdateRequest } from '../models/requestBodies/UpdateUserRequestBody';
import { type CreateBookingRequest } from '../models/booking/CreateBookingRequest';
import { DayAvailabilityResponse } from '../models/booking/DayAvailabilityResponse';
import { ActivityResponse } from '../models/booking/ActivitiesResponse';
import { type MonthAvailabilityResponse } from '../models/booking/MonthAvailabilityResponse';

const getIdentityBaseUrl = (): string => {
  const explicit = process.env.REACT_APP_IDENTITY_API_BASE_URL;
  if (!explicit) {
    throw new Error('Missing REACT_APP_IDENTITY_API_BASE_URL environment variable');
  }
  return explicit.endsWith('/') ? explicit : explicit + '/';
};

const getAuthBaseUrl = (): string => {
  const explicit = process.env.REACT_APP_IDENTITY_API_BASE_URL;
  if (!explicit) {
    throw new Error('Missing REACT_APP_IDENTITY_API_BASE_URL environment variable');
  }
  return explicit.endsWith('/') ? explicit : explicit + '/';
};

export class IdentityClient extends BaseClient {
  private identityBaseUrl: string;
  private authBaseUrl: string;

  constructor() {
    super();
    this.identityBaseUrl = getIdentityBaseUrl();
    this.authBaseUrl = getAuthBaseUrl();
  }

  // GET: {base}/{GUID}/verify
  public async verifyEmail(linkGuid: string): Promise<Response> {
    const url = `${this.identityBaseUrl}users/${linkGuid}/verify`;
    return fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
  }

  public async confirmEmailChange(changeId: string): Promise<Response> {
    const url =`${this.identityBaseUrl}users/change-email/${changeId}/verify`;

    return fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
  }

  public async getUserProfile(): Promise<User> {
    const url = `${this.identityBaseUrl}users/me`;
    const response = await this.fetchWithAuth(url, {
      method: 'GET'
    });

    if (!response.ok) {
      throw new Error('Failed to get user profile');
    }

    return await response.json();
  }

  // POST: {base}/set-password
  public async setPassword(payload: SetPasswordRequestBody): Promise<string | null> {
    const url = `${this.identityBaseUrl}users/set-password`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      return null;
    }
    const jwt = await response.text();
    if (jwt) {
      localStorage.setItem('authToken', jwt);
    }
    return jwt;
  }

  // POST: {authBase}/auth/login, store token in localStorage on success
  public async login(body: LoginRequestBody): Promise<string | null> {
    const url = `${this.authBaseUrl}auth/login`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      return null;
    }
    const jwt = await response.text();
    if (jwt) {
      localStorage.setItem('authToken', jwt);
    }
    return jwt;
  }

  public async refreshToken(): Promise<string> {
    const url = `${this.authBaseUrl}auth/refresh`;
    const response = await this.fetchWithAuth(url, { method: 'POST' });
    if (response.ok) {
      return await response.text();
    }
    throw new Error('Failed to refresh token');
  }

  public async createUser(payload: CreateNewUserRequestBody): Promise<Response> {
    const url = `${this.identityBaseUrl}users/create`;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  public async linkUserToDriver(body: LinkUserToDriverRequestBody): Promise<void> {
    const url = `${this.identityBaseUrl}users/link-driver`;

    const response = await this.fetchWithAuth(url, { method: 'PATCH', body: JSON.stringify(body) });
    if (response.status === 204) {
      return;
    } else {
      throw new Error(`Failed to link user to driver (${response.status})`);
    }
  }

  public async resetPassword(email: string): Promise<Response> {
    const url = `${this.identityBaseUrl}users/reset-password`;
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
  }

  public async updatePassword(payload: SetPasswordRequestBody): Promise<Response> {
    const url = `${this.identityBaseUrl}users/update`;
    return this.fetchWithAuth(url, { method: 'POST', body: JSON.stringify(payload) });
  }

  public async updateUserEmail(payload: UserEmailUpdateRequest): Promise<null> {
    const url = `${this.identityBaseUrl}users/change-email`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Failed to update user email (${response.status})`);
    }

    if (response.status === 204) {
      return null;
    }
    
    throw new Error(`Unexpected response status: ${response.status}`);
  }

  public async getUserBalance(userId: string): Promise<number | null> {
    const url = `${this.identityBaseUrl}wallet/user-balance`;

    const response = await this.fetchWithAuth(url, {
      method: 'GET'
    });

    if (!response.ok) {
      throw new Error('Failed to get user balance');
    }

    const balance: number = await response.json();
    return balance;
  }

  public async getCreditProducts(): Promise<CreditPackageProduct[]> {
    const url = `${this.identityBaseUrl}products`;
    const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' }});
    if (!response.ok) {
      throw new Error('Failed to get credit products');
    }
    return await response.json();
  }

  public async intentPayment(payload: PaymentIntentRequest): Promise<PaymentIntentResponse> {
    const url = `${this.identityBaseUrl}api/stripe/intent`;
    const response = await fetch(url, { 
      method: 'POST', 
      headers: { 'Content-Type': 'application/json' }, 
      body: JSON.stringify(payload) 
    });
    if (!response.ok) {
      throw new Error('Failed to create payment intent');
    }
    return await response.json();
  }

  public async getDayAvailability(location: string, date: string, activityId: string | null = null): Promise<DayAvailabilityResponse> {
    const search = new URLSearchParams({
      Location: location,
      Date: date,
    });
    if (activityId != null) {
      search.set('ActivityId', activityId);
    }

    const url = `${this.identityBaseUrl}bookings/availability/day?${search.toString()}`;
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load day availability (${response.status})`);
    }
    return (await response.json()) as DayAvailabilityResponse;
  }

  public async getMonthAvailability(location: string, year: number, month: number, activityId: string): Promise<MonthAvailabilityResponse> {
    const search = new URLSearchParams({
      Location: location,
      Year: String(year),
      Month: String(month),
      ActivityId: activityId,
    });
    const url = `${this.identityBaseUrl}bookings/availability/month?${search.toString()}`;
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load month availability (${response.status})`);
    }
    return (await response.json()) as MonthAvailabilityResponse;
  }

  public async getActivities(location: string): Promise<ActivityResponse> {
    const search = new URLSearchParams({ Location: location });

    const url = `${this.identityBaseUrl}bookings/activities?${search.toString()}`;
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load activities (${response.status})`);
    }
    return (await response.json()) as ActivityResponse;
  }

  // POST: {identityBaseUrl}/bookings
  public async CreateBooking(request: CreateBookingRequest): Promise<void> {
    const url = `${this.identityBaseUrl}bookings`;
    const response = await this.fetchWithAuth(url, { method: 'POST', body: JSON.stringify(request) });
    if (!response.ok) {
      throw new Error(`Failed to create booking (${response.status})`);
    }
  }

  public async patchUser(location: string): Promise<void> {
    const url = `${this.identityBaseUrl}users`;
    const response = await this.fetchWithAuth(url, { method: 'PATCH', body: JSON.stringify({ location }) });
    if (!response.ok) {
      throw new Error(`Failed to update user (${response.status})`);
    }
  }
}


const identityApi = new IdentityClient();
export default identityApi;


